import type { ReactNode } from "react";
import { cn } from "@/shared/lib/cn";
import { BrandFootnote } from "../BrandFootnote/BrandFootnote";

export interface AuthLayoutProps {
  children: ReactNode;
  /** Ancho máximo de la columna central. */
  width?: "sm" | "md" | "lg";
  className?: string;
}

const widthClasses = {
  sm: "max-w-sm",
  md: "max-w-md",
  /**
   * Para formularios que se reparten en dos columnas en escritorio.
   * Deliberadamente NO más ancho: la card ya es el objeto pesado de la
   * composición y, pasada esta anchura, tapa el centro de la fotografía
   * y la pantalla se queda sin respiro alrededor.
   */
  lg: "max-w-xl",
} as const;

/**
 * El armazón de las pantallas de acceso: la imagen a pantalla completa
 * detrás de todo, el contenido centrado encima y el pie de copyright.
 *
 * La imagen va en un `<div>` con `background-image` y no en un `<img>`:
 * es decoración pura y no debe aparecer en el árbol de accesibilidad ni
 * pedir un `alt` que nadie sabría qué escribir.
 */
export function AuthLayout({ children, width = "md", className }: AuthLayoutProps) {
  return (
    <div className="relative min-h-dvh overflow-hidden bg-ds-cream">
      {/*
        `fixed`, no `absolute`. Con `absolute` el fondo mide lo que mide el
        CONTENIDO: en el login cabía en la pantalla y se veía bien, pero en
        el registro —más alto— el contenedor crecía y `bg-cover` reescalaba
        la imagen a esa altura mayor, así que la misma foto se veía más
        acercada y recortada en una pantalla que en la otra. Anclado al
        viewport siempre encuadra igual, mida lo que mida el formulario.
      */}
      <div
        aria-hidden="true"
        className="fixed inset-0 bg-cover bg-center"
        style={{ backgroundImage: "url('/assets/login-hero.webp')" }}
      />

      <div
        className={cn(
          // `dvh` y no `vh`: en móvil la barra del navegador aparece y
          // desaparece, y `vh` se queda con la altura de cuando cargó.
          "relative flex min-h-dvh items-center justify-center px-6 py-5 sm:py-8",
          className,
        )}
      >
        {/*
          `min-w-0` es preventivo: esta columna es un ítem flex, y un ítem
          flex no se encoge por debajo del ancho mínimo de su contenido.
          Hoy nada dentro lo fuerza, pero el día que entre una tabla, un
          código largo o una URL sin espacios, la card se desbordaría por
          la derecha en móvil en vez de adaptarse.
        */}
        <div className={cn("w-full min-w-0", widthClasses[width])}>
          {/* Logotipo pequeño arriba, solo en móvil: en escritorio la
              imagen de fondo ya lleva la marca. */}
          <div className="mb-5 flex flex-col items-center text-center md:hidden">
            <img
              src="/assets/itb-logo.webp"
              alt="It's The Best"
              className="h-10 w-auto"
              width={160}
              height={40}
            />
            <div className="mt-3 text-[11px] font-medium uppercase tracking-ds-label-wide text-ds-purple">
              Plataforma Premium
            </div>
          </div>

          {children}

        </div>

        {/*
          El pie sale del FLUJO y se ancla abajo. Es cromo decorativo, no
          contenido: sumando su alto al de la columna empujaba el
          formulario y era lo primero que sacaba un paso de la pantalla.
          Anclado, se ve igual y deja de competir por el espacio.
        */}
        <BrandFootnote className="absolute inset-x-0 bottom-3 mx-auto" />
      </div>
    </div>
  );
}
